import React from 'react';
import { Calendar } from 'lucide-react';
import { getOrderUrgency } from '../kpiUtils';
import AIInsights from './AIInsights';

const STATUS_STYLE = {
  on_track: 'bg-emerald-100 text-emerald-700',
  at_risk:  'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
  overdue:  'bg-red-200 text-red-800',
  fulfilled:'bg-slate-100 text-slate-600',
};
const STATUS_LABEL = {
  on_track: 'On Track', at_risk: 'At Risk',
  critical: 'Critical', overdue: 'Overdue', fulfilled: 'Fulfilled',
};

const OrderCard = ({ order }) => {
  const isFulfilled = String(order.status || '').toLowerCase() === 'fulfilled';
  const urgency = isFulfilled
    ? { bucket: 'fulfilled', daysUntilDue: null }
    : getOrderUrgency(order.due_date);
  const style = STATUS_STYLE[urgency.bucket] || STATUS_STYLE.on_track;
  const dueStr = order.due_date
    ? new Date(order.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : '—';

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-1 shadow-sm">
      <div className="flex items-start justify-between gap-1">
        <p className="font-semibold text-slate-800 text-xs leading-tight">{order.order_number}</p>
        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded whitespace-nowrap ${style}`}>
          {STATUS_LABEL[urgency.bucket] || 'Pending'}
        </span>
      </div>
      <p className="text-xs text-slate-500 truncate">{order.customer_name}</p>
      <div className="flex items-center gap-1 text-[10px] text-slate-400">
        <Calendar className="w-3 h-3" />
        <span>Due {dueStr}</span>
        {urgency.daysUntilDue !== null && !isFulfilled && (
          <span className={`ml-1 font-medium ${
            urgency.daysUntilDue < 0   ? 'text-red-600'
            : urgency.daysUntilDue <= 2 ? 'text-red-500'
            : urgency.daysUntilDue <= 5 ? 'text-amber-500'
            : 'text-emerald-600'
          }`}>
            ({urgency.daysUntilDue < 0
              ? `${Math.abs(urgency.daysUntilDue)}d overdue`
              : `${urgency.daysUntilDue}d left`})
          </span>
        )}
      </div>
      {order.blocking_reason && (
        <p className="text-[10px] text-red-600 truncate">⚠ {order.blocking_reason}</p>
      )}
      <span className="inline-block text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded capitalize">
        {order.order_category || order.type || 'order'}
      </span>
    </div>
  );
};

const KanbanColumn = ({ title, orders, accentClass }) => (
  <div className="flex flex-col gap-2 min-w-[155px]">
    <div className={`flex items-center gap-1.5 pb-1 border-b-2 ${accentClass}`}>
      <span className="text-xs font-bold uppercase tracking-wide text-slate-600">{title}</span>
      <span className="text-xs bg-slate-100 rounded-full px-1.5 font-medium text-slate-600">{orders.length}</span>
    </div>
    {orders.length === 0
      ? <p className="text-xs text-slate-400 italic">None</p>
      : orders.map((o) => <OrderCard key={o.id || o.order_id} order={o} />)}
  </div>
);

const TimelineDot = ({ order }) => {
  const isFulfilled = String(order.status || '').toLowerCase() === 'fulfilled';
  const { bucket } = isFulfilled
    ? { bucket: 'fulfilled' }
    : getOrderUrgency(order.due_date);
  const dotColor =
    bucket === 'overdue'  ? 'bg-red-600'
    : bucket === 'critical' ? 'bg-red-400'
    : bucket === 'at_risk'  ? 'bg-amber-400'
    : bucket === 'fulfilled'? 'bg-slate-200'
    : 'bg-emerald-400';
  const dueStr = order.due_date
    ? new Date(order.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : '—';

  return (
    <div className="flex flex-col items-center gap-1 min-w-[52px]">
      <div
        className={`w-3 h-3 rounded-full ${dotColor} ring-2 ring-white`}
        title={order.order_number}
      />
      <span className="text-[9px] text-slate-500 text-center leading-tight">
        {order.order_number?.slice(-5)}
      </span>
      <span className="text-[9px] text-slate-400 text-center">{dueStr}</span>
    </div>
  );
};

export default function OrderIntelligence({ data }) {
  if (!data) return null;

  const { upcomingOrders, fulfillableCount, atRiskCount } = data;

  const pending    = upcomingOrders.filter((o) =>
    String(o.status || '').toLowerCase() === 'pending' &&
    getOrderUrgency(o.due_date).bucket === 'on_track'
  );
  const inProgress = upcomingOrders.filter((o) =>
    ['in progress', 'in_progress'].includes(String(o.status || '').toLowerCase())
  );
  const atRisk     = upcomingOrders.filter((o) => {
    const b = getOrderUrgency(o.due_date).bucket;
    return (b === 'at_risk' || b === 'critical' || b === 'overdue') &&
      String(o.status || '').toLowerCase() !== 'fulfilled';
  });
  const fulfilled  = upcomingOrders.filter((o) =>
    String(o.status || '').toLowerCase() === 'fulfilled'
  );

  const sortedByDue = [...upcomingOrders].sort(
    (a, b) => new Date(a.due_date) - new Date(b.due_date)
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <h3 className="text-base font-semibold text-slate-800">Order Intelligence</h3>
        <div className="flex gap-3 text-xs">
          <span className="text-emerald-700 font-medium">{fulfillableCount} fulfillable</span>
          <span className="text-red-600 font-medium">{atRiskCount} at risk</span>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-start">
        {/* Kanban — left 2/3 */}
        <div className="lg:col-span-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
            Order Status Board
          </p>
          {upcomingOrders.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No upcoming orders found.</p>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-2">
              <KanbanColumn title="Pending"     orders={pending}    accentClass="border-slate-300" />
              <KanbanColumn title="In Progress" orders={inProgress} accentClass="border-blue-400"  />
              <KanbanColumn title="At Risk"     orders={atRisk}     accentClass="border-red-400"   />
              <KanbanColumn title="Fulfilled"   orders={fulfilled}  accentClass="border-emerald-400"/>
            </div>
          )}
        </div>

        {/* Right column — timeline + AI insights */}
        <div className="lg:col-span-1 flex flex-col gap-5">
          {upcomingOrders.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
                Deadline Timeline
              </p>
              <div className="relative flex items-start gap-2 overflow-x-auto py-2 flex-wrap">
                <div className="absolute top-3.5 left-0 right-0 h-0.5 bg-slate-200" />
                {sortedByDue.map((o) => (
                  <TimelineDot key={o.id} order={o} />
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-400">
                {[
                  { color: 'bg-red-600',     label: 'Overdue'   },
                  { color: 'bg-amber-400',   label: 'At Risk'   },
                  { color: 'bg-emerald-400', label: 'On Track'  },
                  { color: 'bg-slate-200',   label: 'Fulfilled' },
                ].map(({ color, label }) => (
                  <span key={label} className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full inline-block ${color}`} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div id="insights" className="flex-1 min-h-0">
            <AIInsights data={data} embedded />
          </div>
        </div>
      </div>
    </div>
  );
}
